import { ExternalLink } from 'lucide-react';
import { DateRangeBadge } from '@ooh/ui/components/date-range-badge';
import type { QueueEntryView, ReservationQueueListLabels } from './reservation-queue-list';

/** Offer name as a link when `offerHref` is available, plain text otherwise, or nothing when there's no offer. */
export function QueueEntryOfferLink({ entry }: { entry: QueueEntryView }) {
  if (entry.offerName === null) return null;
  if (entry.offerHref) {
    return (
      <a
        href={entry.offerHref}
        onClick={(e) => e.stopPropagation()}
        className="flex items-center gap-1 text-2xs text-primary hover:text-primary/80 max-w-[200px] group"
      >
        <span className="truncate">{entry.offerName}</span>
        <ExternalLink className="h-3 w-3 shrink-0 opacity-60 group-hover:opacity-100" />
      </a>
    );
  }
  // Offer exists but no link was supplied — still show the name, just not clickable.
  return <span className="text-2xs text-muted-foreground truncate">{entry.offerName}</span>;
}

/** "Ty" chip marking the current user's own entry — shared by whichever line ends up carrying it. */
export function QueueEntryOwnBadge({ entryId, label }: { entryId: number; label: string }) {
  return (
    <span
      data-testid={`queue-entry-own-badge-${entryId}`}
      className="shrink-0 rounded-full bg-primary/10 text-primary px-1.5 py-0 text-2xs font-semibold"
    >
      {label}
    </span>
  );
}

/**
 * Optional extra meta line: salesperson, join date, expected promotion date.
 * Renders nothing unless the caller opted in by supplying the matching
 * `QueueEntryView` field AND its label — so the single-unit panel
 * (CU-86cb3x6y6) keeps its exact original two-line row, and only the
 * cross-unit overview (CU-86cb7ctfw) pays for the denser variant.
 */
export function QueueEntryMeta({
  entry,
  labels,
}: {
  entry: QueueEntryView;
  labels: ReservationQueueListLabels;
}) {
  const parts: { key: string; label: string; value: string }[] = [];
  if (entry.salespersonName != null && labels.salespersonLabel != null) {
    parts.push({
      key: 'salesperson',
      label: labels.salespersonLabel,
      value: entry.salespersonName,
    });
  }
  if (entry.joinedAtLabel != null && labels.joinedAtLabel != null) {
    parts.push({ key: 'joined', label: labels.joinedAtLabel, value: entry.joinedAtLabel });
  }
  if (entry.promotionEtaLabel != null && labels.promotionEtaLabel != null) {
    parts.push({ key: 'eta', label: labels.promotionEtaLabel, value: entry.promotionEtaLabel });
  }
  if (entry.brand != null && labels.brandLabel != null) {
    parts.push({ key: 'brand', label: labels.brandLabel, value: entry.brand });
  }
  if (parts.length === 0) return null;

  return (
    <p
      data-testid={`queue-entry-meta-${entry.id}`}
      className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-2xs text-muted-foreground"
    >
      {parts.map((part) => (
        <span key={part.key} className="whitespace-nowrap">
          {part.label} <span className="text-foreground/70">{part.value}</span>
        </span>
      ))}
    </p>
  );
}

/**
 * Identity block: primary line (client, offer, or last-resort submitter — see
 * the component doc for why NOT userName-first), the offer link + date
 * badge, and the small submitter attribution line (skipped when the
 * submitter is already the primary line, so the name never repeats).
 * Extracted from `QueueEntryRow` to keep its complexity within budget.
 */
export function QueueEntryIdentity({
  entry,
  isOwn,
  labels,
}: {
  entry: QueueEntryView;
  isOwn: boolean;
  labels: ReservationQueueListLabels;
}) {
  const primaryLabel = entry.clientName ?? entry.offerName ?? entry.userName;
  const showAttribution = entry.clientName != null || entry.offerName != null;

  return (
    <div className="min-w-0 flex-1">
      <p className="text-[13px] font-semibold text-foreground truncate flex items-center gap-1.5">
        {primaryLabel}
        {!showAttribution && isOwn ? (
          <QueueEntryOwnBadge entryId={entry.id} label={labels.ownBadgeLabel} />
        ) : null}
      </p>
      <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
        <QueueEntryOfferLink entry={entry} />
        {entry.offerName === null ? (
          <span className="text-2xs text-muted-foreground truncate">{labels.noOfferLabel}</span>
        ) : null}
        {/* Same read-only period-badge treatment as the client offer
            public-link page's period chips — CU-86cb3x6y6 follow-up. */}
        <DateRangeBadge
          data-testid={`queue-entry-dates-${entry.id}`}
          label={`${entry.dateFrom} – ${entry.dateTo}`}
        />
      </div>
      {showAttribution ? (
        <p className="mt-0.5 flex items-center gap-1.5 text-2xs text-muted-foreground truncate">
          {labels.submittedByLabel} {entry.userName}
          {isOwn ? <QueueEntryOwnBadge entryId={entry.id} label={labels.ownBadgeLabel} /> : null}
        </p>
      ) : null}
      <QueueEntryMeta entry={entry} labels={labels} />
    </div>
  );
}
